/**
 * FullDdlCard — the full-mode read-only DDL fact card (EDG-06..10). Full mode
 * is the user's most spacious surface, yet its DDL fallback used to be
 * fact-free ("STATEMENT CREATE" + reads/writes). This renders the SAME rich
 * facts the compact popover shows, driven by the SAME pure, context-free model
 * (`buildDdlCardModel`) and the SAME copy (`vqeCopy.ddlCard`) — only the
 * typography is full-mode `text-sm` instead of the compact `text-xs`.
 *
 * It is the fallback's ONLY message in the full tab: the readiness banner is
 * hidden while a fallback panel is on screen and there is no separate hint
 * box, so the card's one `hint` line says why it is read-only and what to do.
 *
 * Honesty floor (mirrors `CompactDdlCard`): the object-name row and the fact
 * list are omitted entirely when empty — never a placeholder or a fabricated
 * fact. The Design-table button renders ONLY for a resolved create/alter-table
 * model (DROP/others carry no button, not a disabled one); when it renders it
 * is enabled iff a `connectionId` is present, otherwise disabled with a
 * specific-reason `VqeTooltip` (never a native `title=`). The hand-off itself
 * (`openDesignTableFromDdlCard`) and its analytics live one level up in
 * `ReadOnlyFallback` — this component only renders the button and forwards the
 * click, matching the `CompactDdlCard` / `CompactVqePopover` split.
 *
 * Emphasis: the workspace header always carries Edit as SQL, so when the card
 * offers Design table that is the card's filled primary and the card's own
 * Edit as SQL is outlined — one solid button, not two "Edit as SQL"s competing.
 */

import { Settings } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { VqeTooltip } from '../common/VqeTooltip';
import { SqlPreviewCode } from '../common/SqlPreviewCode';
import { StatementTextDisclosure } from './StatementTextDisclosure';
import type { DdlCardModel } from '../../compact/compactDdlCard';
import { vqeCopy } from '../../ux/vqeCopy';

export interface FullDdlCardProps {
  readonly model: DdlCardModel;
  /** Statement source text for the show/hide colored preview. */
  readonly body: string;
  /** Close the card + focus the same statement in the SQL editor. */
  readonly onEditAsSql: () => void;
  /** Open the Table Designer for `model.designTable`. Unused when it is `null`. */
  readonly onDesignTable: () => void;
  /** Gates the Design-table button — `null`/`undefined` disables it with a reason. */
  readonly connectionId?: string | null;
  /** The card's one explanatory line (why it is read-only + what to do). */
  readonly hint: string;
}

export function FullDdlCard({ model, body, onEditAsSql, onDesignTable, connectionId, hint }: FullDdlCardProps) {
  const designTableTooltip = !connectionId
    ? vqeCopy.ddlCard.tooltips.designTableNoConnection
    : model.designTable?.mode === 'create'
      ? vqeCopy.ddlCard.tooltips.designTableCreate
      : vqeCopy.ddlCard.tooltips.designTableAlter;

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm" data-testid="vqe-readonly-ddl-card">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className="rounded bg-muted px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-foreground"
          data-testid="vqe-readonly-fallback-kind"
        >
          {model.kindLabel}
        </span>
        {model.objectName ? (
          <span
            className="truncate font-mono text-sm text-muted-foreground"
            data-testid="vqe-readonly-fallback-object"
          >
            {model.objectName}
          </span>
        ) : null}
      </div>

      {model.facts.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm text-muted-foreground">
          {model.facts.map((fact, index) => (
            <li key={index} data-testid="vqe-readonly-fallback-fact">
              {fact}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-3 text-sm text-muted-foreground" data-testid="vqe-readonly-fallback-hint">
        {hint}
      </p>

      <StatementTextDisclosure testIdPrefix="vqe-readonly-fallback">
        <SqlPreviewCode sql={body} />
      </StatementTextDisclosure>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {model.designTable ? (
          <VqeTooltip content={designTableTooltip}>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onDesignTable}
              disabled={!connectionId}
              aria-label={vqeCopy.ddlCard.actions.designTable}
              data-testid="vqe-ddl-design-table"
            >
              <Settings className="h-4 w-4" />
              {vqeCopy.ddlCard.actions.designTable}
            </Button>
          </VqeTooltip>
        ) : null}
        <VqeTooltip content={vqeCopy.ddlCard.tooltips.editAsSql}>
          <Button
            type="button"
            variant={model.designTable ? 'outline' : 'default'}
            size="sm"
            onClick={onEditAsSql}
            data-testid="vqe-readonly-fallback-edit-as-sql"
          >
            {vqeCopy.actions.editAsSql}
          </Button>
        </VqeTooltip>
      </div>
    </div>
  );
}
